/**
 * What the Reference node accepts: one image, audio file or video, detected from
 * the file and validated against the limits the providers and the app can handle.
 */

import { audioExtensionFromMime, validateAudioFile } from './audioReference';

export type ReferenceKind = 'image' | 'audio' | 'video';

export const IMAGE_MAX_BYTES = 25 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 100 * 1024 * 1024;

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
const AUDIO_EXTENSIONS = ['mp3', 'wav'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v'];

/** Accept list for the file picker. */
export const REFERENCE_ACCEPT = [
    'image/png', 'image/jpeg', 'image/webp', 'image/gif',
    'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave',
    'video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v',
    ...IMAGE_EXTENSIONS.map(e => `.${e}`), ...AUDIO_EXTENSIONS.map(e => `.${e}`), ...VIDEO_EXTENSIONS.map(e => `.${e}`),
].join(',');

const extensionOf = (name: string): string => {
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

/** The kind of reference a file would become, from its MIME type first and its extension second. */
export const detectReferenceKind = (file: { type: string; name: string }): ReferenceKind | null => {
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('audio/')) return 'audio';
    if (type.startsWith('video/')) return 'video';
    const ext = extensionOf(file.name || '');
    if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
    if (AUDIO_EXTENSIONS.includes(ext)) return 'audio';
    if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
    return null;
};

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

/** An image the app can show and send on: PNG, JPEG, WebP or GIF, by MIME type or, when that is missing, by extension. */
const isSupportedImage = (file: { type: string; name: string }): boolean => {
    const type = (file.type || '').toLowerCase();
    return type ? SUPPORTED_IMAGE_TYPES.includes(type) : IMAGE_EXTENSIONS.includes(extensionOf(file.name || ''));
};

const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];

/** A video the app can play and send on: MP4, MOV or WebM, by MIME type or, when that is missing, by extension. */
const isSupportedVideo = (file: { type: string; name: string }): boolean => {
    const type = (file.type || '').toLowerCase();
    return type ? SUPPORTED_VIDEO_TYPES.includes(type) : VIDEO_EXTENSIONS.includes(extensionOf(file.name || ''));
};

const formatMb = (bytes: number): string => `${Math.round(bytes / 1024 / 1024)} MB`;

/** Which kind a file is and, when it cannot be used, why. */
export const validateReferenceFile = (file: File): { kind: ReferenceKind; problem: null } | { kind: ReferenceKind | null; problem: string } => {
    const kind = detectReferenceKind(file);
    if (!kind) return { kind: null, problem: 'Choose an image (PNG, JPG, WebP, GIF), an audio file (MP3, WAV) or a video (MP4, MOV, WebM).' };
    if (kind === 'audio') {
        const problem = validateAudioFile(file);
        return problem ? { kind, problem } : { kind, problem: null };
    }
    if (kind === 'image' && !isSupportedImage(file)) {
        // Dropped files skip the picker's list; other image types (SVG, HEIC) cannot be shown or sent everywhere
        return { kind, problem: 'Use a PNG, JPG, WebP or GIF image.' };
    }
    if (kind === 'image' && file.size > IMAGE_MAX_BYTES) {
        return { kind, problem: `This image is ${formatMb(file.size)}; images can be at most ${formatMb(IMAGE_MAX_BYTES)}.` };
    }
    if (kind === 'video' && !isSupportedVideo(file)) {
        return { kind, problem: 'Use an MP4, MOV or WebM video.' };
    }
    if (kind === 'video' && file.size > VIDEO_MAX_BYTES) {
        return { kind, problem: `This video is ${formatMb(file.size)}; videos can be at most ${formatMb(VIDEO_MAX_BYTES)}. Trim it first or use a smaller export.` };
    }
    return { kind, problem: null };
};

/** File extension for a saved video, from its MIME type or name. */
export const videoExtensionFromMime = (mimeType: string, fileName = ''): 'mp4' | 'mov' | 'webm' => {
    const type = (mimeType || '').toLowerCase();
    if (type.includes('quicktime')) return 'mov';
    if (type.includes('webm')) return 'webm';
    if (type.includes('mp4') || type.includes('m4v')) return 'mp4';
    const ext = extensionOf(fileName);
    if (ext === 'mov') return 'mov';
    if (ext === 'webm') return 'webm';
    return 'mp4';
};

/** File extension for a saved image, from its MIME type or name. */
export const imageExtensionFromMime = (mimeType: string, fileName = ''): 'png' | 'jpg' | 'webp' | 'gif' => {
    const type = (mimeType || '').toLowerCase();
    if (type.includes('jpeg') || type.includes('jpg')) return 'jpg';
    if (type.includes('webp')) return 'webp';
    if (type.includes('gif')) return 'gif';
    if (type.includes('png')) return 'png';
    const ext = extensionOf(fileName);
    if (ext === 'jpg' || ext === 'jpeg') return 'jpg';
    if (ext === 'webp') return 'webp';
    if (ext === 'gif') return 'gif';
    return 'png';
};

/** The kind a Reference node currently holds, from its data. */
export const inferReferenceKind = (data: { referenceKind?: ReferenceKind; imageUrl?: string; audioUrl?: string; videoUri?: string }): ReferenceKind | null => {
    if (data.referenceKind === 'image' && data.imageUrl) return 'image';
    if (data.referenceKind === 'audio' && data.audioUrl) return 'audio';
    if (data.referenceKind === 'video' && data.videoUri) return 'video';
    if (data.audioUrl) return 'audio';
    if (data.videoUri) return 'video';
    if (data.imageUrl) return 'image';
    return null;
};

export type ReferenceStorageCategory = 'Videos' | 'Audio' | 'Uploaded_Images';

const STORAGE_CATEGORY: Record<ReferenceKind, ReferenceStorageCategory> = { video: 'Videos', audio: 'Audio', image: 'Uploaded_Images' };

/**
 * The category folder and file name a Reference node's media is kept under. The copy in
 * the local auto-save folder and the cloud copy the workspace save uploads share them,
 * which is what lets the app find the file on disk instead of downloading it.
 */
export const referenceStorageName = (kind: ReferenceKind, stamp: number, mimeType: string, fileName: string): {
    category: ReferenceStorageCategory;
    extension: string;
    fileName: string;
} => {
    const extension = kind === 'video' ? videoExtensionFromMime(mimeType, fileName)
        : kind === 'audio' ? audioExtensionFromMime(mimeType, fileName)
        : imageExtensionFromMime(mimeType, fileName);
    const category = STORAGE_CATEGORY[kind];
    return { category, extension, fileName: `${category.toLowerCase()}_${stamp}.${extension}` };
};

/** The stamp and extension of a name made by referenceStorageName for `category`, or null for any other name. */
export const parseReferenceStorageName = (name: string | undefined, category: ReferenceStorageCategory): { stamp: number; extension: string } | null => {
    const match = typeof name === 'string' ? /^([a-z_]+)_(\d+)\.([a-z0-9]+)$/.exec(name) : null;
    if (!match || match[1] !== category.toLowerCase()) return null;
    return { stamp: Number(match[2]), extension: match[3] };
};
